/**
 * Formatting.
 *
 * Two rules from the product, applied once here rather than per screen:
 *
 * - **Dates are unambiguous** (NFR-10.5.3, "Dates unambiguous"). Every date is
 *   "12 Sep 2026", never 12/09 or 09/12 - the month is always a word, because an
 *   admin reading a Nepali and a British date side by side cannot tell them apart
 *   otherwise.
 * - **Instants are rendered in the Kathmandu day.** The product's calendar is
 *   `app_today()` = `now() AT TIME ZONE 'Asia/Kathmandu'`, so quotas, referral
 *   days and weight dates all belong to that day. An admin tool that rendered
 *   `created_at` in the viewer's own timezone would show a registration landing on
 *   one day and counting against another. Pinning the zone keeps the screen and
 *   the database agreeing about which day it is.
 */

const TIME_ZONE = "Asia/Kathmandu";

const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: TIME_ZONE,
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TIME_ZONE,
});

const timeFormatter = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TIME_ZONE,
});

type DateLike = string | null | undefined;

function parse(value: DateLike): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "12 Sep 2026". A `DATE` column arrives as "2026-09-12" and parses as UTC
 *  midnight, which is the same calendar day in Kathmandu (+05:45) - so a
 *  date-only value is printed as sent, not shifted. */
export function formatDate(value: DateLike): string {
  const date = parse(value);
  return date ? dateFormatter.format(date) : "-";
}

/** "12 Sep 2026, 14:32" in Kathmandu. */
export function formatDateTime(value: DateLike): string {
  const date = parse(value);
  return date ? dateTimeFormatter.format(date) : "-";
}

/** "14:32" in Kathmandu. */
export function formatTime(value: DateLike): string {
  const date = parse(value);
  return date ? timeFormatter.format(date) : "-";
}

/** The value, or an em dash. Used for every nullable field on a detail screen,
 *  so "absent" is visually distinct from "empty string". */
export function orDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const text = String(value).trim();
  return text === "" ? "-" : text;
}

/** "NPR 12,000.00" → "NPR 12,000", and "NPR 12,000.50" kept exact.
 *
 *  Amounts are `NUMERIC` and arrive as strings, so they are never put through a
 *  float - the trailing zeros are trimmed by text surgery, which cannot lose a
 *  paisa. */
export function formatMoney(amount: string | number | null | undefined, currency = "NPR"): string {
  if (amount === null || amount === undefined || amount === "") return "-";
  const raw = String(amount);
  const negative = raw.startsWith("-");
  const [whole = "0", fraction] = raw.replace(/^-/, "").split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const trimmedFraction = fraction ? fraction.replace(/0+$/, "") : "";
  return `${negative ? "-" : ""}${currency} ${grouped}${trimmedFraction ? `.${trimmedFraction}` : ""}`;
}

/**
 * "account.approve" → "Account approve".
 *
 * The audit vocabulary (`services/audit_service.py`) is free text in the schema
 * and colon-free by convention, so this is a rendering convenience only. The raw
 * code is shown alongside it, because the identifier is the precise thing and a
 * prettifier that hid it would make the log harder to filter by hand.
 */
export function humanizeAction(code: string): string {
  const [entity, ...rest] = code.split(".");
  const words = [entity, ...rest].filter(Boolean).join(" ").replace(/[._]/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "family_biz" → "Family Biz"; "disable_requested" → "Disable requested". */
export function humanizeStatus(status: string | null | undefined): string {
  if (!status) return "-";
  const spaced = status.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** "SK" for "Shreya Karki" - the avatar placeholder in a table cell. */
export function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? "?";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return (first + last).toUpperCase();
}

/** 1234 → "1,234". For counts in tables and tiles. */
export function formatCount(value: number): string {
  return value.toLocaleString("en-GB");
}

/** "3 days ago", "in 12 days", "today" - for the audit log and the dashboard.
 *  Deliberately coarse: an exact timestamp is shown next to it. */
export function relativeDay(value: DateLike): string {
  const date = parse(value);
  if (!date) return "-";
  const today = new Date();
  const diffDays = Math.round(
    (startOfDay(date).getTime() - startOfDay(today).getTime()) / 86_400_000,
  );
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays === -1) return "yesterday";
  if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
  return `in ${diffDays} days`;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/** Today in Kathmandu, as `YYYY-MM-DD` - the default for a report's `to_date`. */
export function kathmanduToday(): string {
  // `en-CA` is the locale whose short date format is already ISO-8601.
  return new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE }).format(new Date());
}

/** `YYYY-MM-DD` for `days` before today, inclusive windows. */
export function kathmanduDaysAgo(days: number): string {
  const base = new Date(`${kathmanduToday()}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() - days);
  return base.toISOString().slice(0, 10);
}

/** Fitzone's minimum membership age; the API refuses a younger date of birth. */
export const MIN_MEMBER_AGE = 16;
/** Oldest date of birth the API accepts (catches a wrong-century typo). */
export const MAX_MEMBER_AGE = 120;

/** `YYYY-MM-DD` for the same Kathmandu calendar day `years` ago; 29 Feb falls back to 28 Feb. */
export function kathmanduYearsAgo(years: number): string {
  const [year = 0, month = 1, day = 1] = kathmanduToday().split("-").map(Number);
  const target = year - years;
  const leap = (target % 4 === 0 && target % 100 !== 0) || target % 400 === 0;
  const safeDay = month === 2 && day === 29 && !leap ? 28 : day;
  return `${target}-${String(month).padStart(2, "0")}-${String(safeDay).padStart(2, "0")}`;
}
